// src/App.js
import "./styles.css";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";

const MENA_ISO3 = new Set([
  "DZA",
  "BHR",
  "EGY",
  "IRN",
  "IRQ",
  "ISR",
  "JOR",
  "KWT",
  "LBN",
  "LBY",
  "MAR",
  "OMN",
  "PSE",
  "QAT",
  "SAU",
  "SYR",
  "TUN",
  "ARE",
  "YEM",
  "TUR",
]);

// Edit per country
const META = {
  DZA: {
    name: "ALGERIA",
    url: "https://your-site.tld/country/algeria",
    regime: "",
    gdp: "",
    issues: [],
  },
  BHR: {
    name: "BAHRAIN",
    url: "https://your-site.tld/country/bahrain",
    regime: "",
    gdp: "",
    issues: [],
  },
  EGY: {
    name: "EGYPT", // <- custom title
    url: "https://www.youtube.com/watch?v=OafcIPFrjfU",
    regime: "Authoritarian",
    gdp: "$475B (nominal, 2023)",
    issues: ["Debt burden", "Inflation"],
  },
  IRN: {
    name: "IRAN",
    url: "https://your-site.tld/country/iran",
    regime: "",
    gdp: "",
    issues: [],
  },
  IRQ: {
    name: "IRAQ",
    url: "https://your-site.tld/country/iraq",
    regime: "",
    gdp: "",
    issues: [],
  },
  ISR: {
    name: "ISRAEL",
    url: "https://your-site.tld/country/israel",
    regime: "",
    gdp: "",
    issues: [],
  },
  JOR: {
    name: "JORDAN",
    url: "https://your-site.tld/country/jordan",
    regime: "",
    gdp: "",
    issues: [],
  },
  KWT: {
    name: "KUWAIT",
    url: "https://your-site.tld/country/kuwait",
    regime: "",
    gdp: "",
    issues: [],
  },
  LBN: {
    name: "LEBANON",
    url: "https://your-site.tld/country/lebanon",
    regime: "",
    gdp: "",
    issues: [],
  },
  LBY: {
    name: "LIBYA",
    url: "https://your-site.tld/country/libya",
    regime: "",
    gdp: "",
    issues: [],
  },
  MAR: {
    name: "MOROCCO",
    url: "https://your-site.tld/country/morocco",
    regime: "",
    gdp: "",
    issues: [],
  },
  OMN: {
    name: "OMAN",
    url: "https://your-site.tld/country/oman",
    regime: "",
    gdp: "",
    issues: [],
  },
  PSE: {
    name: "OCCUPIED PALESTINIAN TERRITORIES",
    url: "https://your-site.tld/country/palestine",
    regime: "",
    gdp: "",
    issues: [],
  },
  QAT: {
    name: "QATAR",
    url: "https://your-site.tld/country/qatar",
    regime: "",
    gdp: "",
    issues: [],
  },
  SAU: {
    name: "SAUDI ARABIA",
    url: "https://your-site.tld/country/saudi-arabia",
    regime: "",
    gdp: "",
    issues: [],
  },
  SYR: {
    name: "SYRIA",
    url: "https://your-site.tld/country/syria",
    regime: "",
    gdp: "",
    issues: [],
  },
  TUN: {
    name: "TUNISIA",
    url: "https://your-site.tld/country/tunisia",
    regime: "",
    gdp: "",
    issues: [],
  },
  ARE: {
    name: "UNITED ARAB EMIRATES",
    url: "https://your-site.tld/country/united-arab-emirates",
    regime: "",
    gdp: "",
    issues: [],
  },
  YEM: {
    name: "YEMEN",
    url: "https://your-site.tld/country/yemen",
    regime: "",
    gdp: "",
    issues: [],
  },
  TUR: {
    name: "TURKEY",
    url: "https://your-site.tld/country/turkey",
    regime: "",
    gdp: "",
    issues: [],
  },
};

const isoFromProps = (p = {}) =>
  String(p.ADM0_A3 ?? p.ISO_A3 ?? p.ISO_A3_EH ?? p.iso_a3 ?? "").toUpperCase();
const displayName = (p = {}) => {
  const iso3 = isoFromProps(p);
  return META[iso3]?.name ?? p.NAME_EN ?? p.ADMIN ?? p.NAME ?? "Unknown";
};

export default function App() {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null); // { iso3, name, regime, gdp, issues[] }
  const geojsonRef = useRef(null);

  useEffect(() => {
    fetch("/ne_countries.json")
      .then((r) => r.json())
      .then((fc) => {
        const features = (fc.features || []).filter((f) =>
          MENA_ISO3.has(isoFromProps(f.properties))
        );
        setData({ type: "FeatureCollection", features });
      })
      .catch((e) => console.error("Fetch error:", e));
  }, []);

  const baseStyle = useMemo(
    () => ({ weight: 0.7, color: "#DC2626", fillOpacity: 0.12 }),
    []
  );
  const style = useCallback(() => baseStyle, [baseStyle]);

  const onEachFeature = useCallback((feature, layer) => {
    const p = feature.properties || {};
    const iso3 = isoFromProps(p);
    const name = displayName(p);

    layer.on({
      mouseover: (e) => {
        const tgt = e.target;
        tgt.setStyle({ weight: 4, fillOpacity: 1 });
        tgt.bringToFront();
        const center = tgt.getBounds().getCenter();
        const tip = L.tooltip({
          className: "country-label",
          direction: "center",
          permanent: true,
        })
          .setLatLng(center)
          .setContent(name)
          .addTo(tgt._map);

        const el = tip.getElement();
        if (el) {
          el.style.opacity = "0";
          requestAnimationFrame(() => (el.style.opacity = "1"));
        }
        tgt._hoverTip = tip;

        const meta = META[iso3] || {};
        setHover({
          iso3,
          name,
          regime: meta.regime || "—",
          gdp: meta.gdp || "—",
          issues: Array.isArray(meta.issues) ? meta.issues : [],
        });
      },
      mouseout: (e) => {
        const tgt = e.target;
        geojsonRef.current?.resetStyle(tgt);
        if (tgt._hoverTip) {
          const el = tgt._hoverTip.getElement();
          if (el) el.style.opacity = "0";
          setTimeout(() => {
            tgt._hoverTip.remove();
            tgt._hoverTip = null;
          }, 600); // match CSS transition
        }
        setHover(null);
      },
      click: () => {
        const url =
          (META[iso3] && META[iso3].url) ||
          `https://your-site.tld/country/${iso3.toLowerCase()}`;
        window.open(url, "_blank");
      },
    });
  }, []);

  return (
    <div className="app">
      <MapContainer center={[28, 35]} zoom={4} className="map">
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <TileLayer
          attribution="Esri"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}.jpg"
        />
        {data && (
          <GeoJSON
            ref={geojsonRef}
            data={data}
            style={style}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>

      <aside className="info-panel">
        {hover ? (
          <>
            <h2>{hover.name}</h2>
            <div className="kv">
              <span>Regime type</span>
              <strong>{hover.regime}</strong>
            </div>
            <div className="kv">
              <span>GDP</span>
              <strong>{hover.gdp}</strong>
            </div>
            <div className="kv">
              <span>Main political issues</span>
              {hover.issues.length ? (
                <ul>
                  {hover.issues.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              ) : (
                <strong>—</strong>
              )}
            </div>
            <p className="hint">Edit values in the META object.</p>
          </>
        ) : (
          <p className="muted">Hover a country.</p>
        )}
      </aside>
    </div>
  );
}
